import { useState } from "react";
import SitePhotos from "@/components/SitePhotos";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import FlowNode, { Connector } from "@/components/FlowNode";
import Drawer from "@/components/Drawer";
import DocSlot from "@/components/DocSlot";
import DocPreview from "@/components/DocPreview";
import Field from "@/components/Field";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ExternalCostChip, ExternalCostForm } from "@/components/ExternalCostCard";
import { AsBuiltsChip, AsBuiltsForm, asBuiltsDocs } from "@/components/AsBuilts";
import { Radio, Send, Construction, BadgeCheck, Cable, Check, Clock3, Circle } from "lucide-react";

function SaveBar({ onSave, busy }) {
  const { readOnly } = useAuth();
  if (readOnly) return null;
  return (
    <div className="flex justify-end mt-4 pt-3 border-t border-gray-100">
      <button data-testid="drawer-save" className="bdx-btn-primary" onClick={onSave} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
    </div>
  );
}

export default function NbnTab({ project, docs, refresh, refreshDocs }) {
  const { readOnly } = useAuth();
  const nbn = project.nbn;
  const [sel, setSel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const att = (keys) => docs.filter((x) => keys.includes(x.linked_to));

  const [appF, setAppF] = useState({ submitted: nbn.application.submitted, date_submitted: nbn.application.date_submitted || "", application_number: nbn.application.application_number || "" });
  const [siteF, setSiteF] = useState({ status: nbn.site_works.status });
  const [compF, setCompF] = useState({ status: nbn.completion.status, completion_date: nbn.completion.completion_date || "" });

  const patch = async (stage, body, msg, keepOpen) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/nbn/${stage}`, body);
      toast.success(msg);
      refresh();
      if (!keepOpen) setSel(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const appDone = nbn.application.submitted;
  const siteDone = nbn.site_works.status === "Completed";
  const compDone = nbn.completion.status === "Completed";
  const stringDone = nbn.pipe_stringing === "Yes";
  // v1.0.18: two independent vertical workstreams — Infrastructure (Pit & Pipe → Pipe Stringing), Application (Application → Official Completion Statement)
  const infraDones = [siteDone, stringDone];
  const infraState = (i) => {
    if (i === 1 && nbn.pipe_stringing === "No") return "overdue";
    return infraDones[i] ? "complete" : infraDones.slice(0, i).every(Boolean) ? "current" : "future";
  };
  const appDones = [appDone, compDone];
  const appState = (i) => (appDones[i] ? "complete" : appDones.slice(0, i).every(Boolean) ? "current" : "future");

  const stringTone = { Pending: "amber", Yes: "green", No: "red" }[nbn.pipe_stringing];

  const appCost = nbn.application_cost || {};
  const meta = {
    application: { title: "NBN Application", status: appDone ? "Applied" : "Pending", tone: appDone ? "green" : "amber" },
    application_cost: {
      title: "NBN Application — VQR Cost",
      status: appCost.mode === "existing_asset" ? "Existing Asset" : appCost.mode === "invoice" ? (appCost.paid ? "Paid" : "Unpaid") : "Not Recorded",
      tone: appCost.mode === "existing_asset" || (appCost.mode === "invoice" && appCost.paid) ? "orange" : appCost.mode === "invoice" ? "red" : "grey",
    },
    site_works: { title: "NBN Pit & Pipe Infrastructure", status: nbn.site_works.status, tone: siteDone ? "green" : nbn.site_works.status === "In Progress" ? "amber" : "grey" },
    completion: { title: "Official Completion Statement", status: nbn.completion.status, tone: compDone ? "green" : nbn.completion.status === "In Progress" ? "amber" : "grey" },
    pipe_stringing: { title: "Infrastructure Pipe Stringing", status: nbn.pipe_stringing, tone: stringTone },
    as_builts: { title: "As Builts — Shared Documents", status: asBuiltsDocs(docs).length ? `${asBuiltsDocs(docs).length} Attached` : "No Documents", tone: asBuiltsDocs(docs).length ? "green" : "grey" },
  };

  return (
    <div data-testid="nbn-tab" className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
      <div className="h-1 bg-[#765297]" />
      <div className="flex flex-col gap-4 border-b border-gray-200 bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between lg:px-7">
        <div className="flex items-center gap-3">
          <div className="bdx-icon-tile h-10 w-10"><Radio size={18} strokeWidth={1.9} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-950">NBN Connection Lifecycle</h2>
            <p className="mt-0.5 text-xs text-gray-500">Infrastructure and Application are independent parallel workstreams</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
        <SitePhotos project={project} roadmap="nbn" />
        <div className="flex flex-wrap items-center gap-4 text-[10px] font-semibold uppercase text-gray-500" data-testid="nbn-roadmap-legend" style={{ letterSpacing: "0.08em" }}>
          <span className="inline-flex items-center gap-1.5"><Check size={12} className="text-emerald-600" /> Complete</span>
          <span className="inline-flex items-center gap-1.5"><Clock3 size={12} className="text-amber-600" /> Current</span>
          <span className="inline-flex items-center gap-1.5"><Circle size={9} className="text-gray-400" /> Future</span>
        </div>
        </div>
      </div>
      <div className="bdx-roadmap-grid px-5 py-8 sm:px-8 lg:px-10 lg:py-10">
      <div className="mx-auto grid w-full max-w-[920px] grid-cols-1 items-start gap-10 md:grid-cols-2">
      <div data-testid="nbn-lane-infrastructure">
        <div className="mb-4 flex items-center justify-center gap-2 text-[10px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.14em" }}>
          <span className="h-px w-10 bg-gray-300" /> NBN Infrastructure <span className="h-px w-10 bg-gray-300" />
        </div>
        <div className="relative">
        <FlowNode
          testId="nbn-node-siteworks"
          title="NBN Pit & Pipe Infrastructure"
          status={nbn.site_works.status} tone={meta.site_works.tone} state={infraState(0)}
          onClick={() => setSel("site_works")}
          attachments={att(["nbn_design"])}
          onViewDoc={setPreview}
          appearance="electrical"
          icon={Construction}
          stageType="Infrastructure Delivery"
        />
        <AsBuiltsChip docs={docs} side="left" testId="nbn-as-builts-chip" onClick={() => setSel("as_builts")} />
        </div>
        <Connector active={siteDone} appearance="electrical" />
        <FlowNode
          testId="nbn-node-stringing"
          title="Infrastructure Pipe Stringing"
          status={nbn.pipe_stringing} tone={stringTone} state={infraState(1)}
          onClick={() => setSel("pipe_stringing")}
          lines={["Have infrastructure pipes been stringed?"]}
          appearance="electrical"
          icon={Cable}
          stageType="Infrastructure Readiness"
        />
      </div>
      <div data-testid="nbn-lane-application">
        <div className="mb-4 flex items-center justify-center gap-2 text-[10px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.14em" }}>
          <span className="h-px w-10 bg-gray-300" /> NBN Application <span className="h-px w-10 bg-gray-300" />
        </div>
        <div className="relative">
        <FlowNode
          testId="nbn-node-application"
          title="NBN Application"
          status={appDone ? "Applied" : "Pending"} tone={appDone ? "green" : "amber"} state={appState(0)}
          onClick={() => setSel("application")}
          attachments={att(["nbn_agreement"])}
          onViewDoc={setPreview}
          appearance="electrical"
          icon={Send}
          stageType="NBN Application"
          lines={[
            nbn.application.application_number && `App No: ${nbn.application.application_number}`,
            nbn.application.date_submitted && `Submitted: ${fmtDate(nbn.application.date_submitted)}`,
            "Independent of infrastructure progress",
          ]}
        />
        <div className="mt-2 flex items-center xl:absolute xl:left-full xl:top-1/2 xl:mt-0 xl:-translate-y-1/2">
          <span className="hidden h-px w-6 shrink-0 bg-gray-300 xl:block" />
          <div className="w-52 shrink-0">
            <ExternalCostChip cost={nbn.application_cost} testId="nbn-ext-cost-chip" onClick={() => setSel("application_cost")} />
          </div>
        </div>
        </div>
        <Connector active={appDone} appearance="electrical" />
        <FlowNode
          testId="nbn-node-completion"
          title="Official Completion Statement"
          status={nbn.completion.status} tone={meta.completion.tone} state={appState(1)}
          onClick={() => setSel("completion")}
          attachments={att(["nbn_form1", "nbn_form2", "nbn_completion"])}
          onViewDoc={setPreview}
          appearance="electrical"
          icon={BadgeCheck}
          stageType="Official Certification"
          lines={[nbn.completion.completion_date && `Completed: ${fmtDate(nbn.completion.completion_date)}`]}
        />
      </div>
      </div>
      </div>

      {sel && (
        <Drawer
          title={meta[sel].title}
          subtitle={`Project ${project.project_number} — ${project.label}`}
          status={meta[sel].status}
          tone={meta[sel].tone}
          onClose={() => setSel(null)}
        >
          {sel === "application_cost" && (
            <ExternalCostForm project={project} scope="nbn_application" cost={nbn.application_cost}
              remitLinkedTo="nbn_remittance" docs={docs} refresh={refresh} refreshDocs={refreshDocs} onClose={() => setSel(null)} />
          )}
          {sel === "as_builts" && <AsBuiltsForm project={project} docs={docs} refreshDocs={refreshDocs} />}
          {sel === "application" && (
            <div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Application Submitted">
                  <label className="flex items-center gap-2 text-sm mt-1.5 cursor-pointer">
                    <input data-testid="nbn-app-submitted" type="checkbox" className="h-4 w-4 accent-[#604184]" disabled={readOnly}
                      checked={!!appF.submitted} onChange={(e) => setAppF({ ...appF, submitted: e.target.checked })} />
                    Submitted
                  </label>
                </Field>
                <Field label="Date Submitted"><input data-testid="nbn-app-date" type="date" className="bdx-input" disabled={readOnly} value={appF.date_submitted} onChange={(e) => setAppF({ ...appF, date_submitted: e.target.value })} /></Field>
                <Field label="Application Number"><input data-testid="nbn-app-number" className="bdx-input font-mono-data" disabled={readOnly} value={appF.application_number} onChange={(e) => setAppF({ ...appF, application_number: e.target.value })} /></Field>
              </div>
              <div className="mt-4">
                <DocSlot projectId={project.id} linkedTo="nbn_agreement" category="NBN Agreement" label="Agreement Document" docs={docs} onUploaded={refreshDocs} />
              </div>
              <SaveBar onSave={() => patch("application", appF, "NBN application saved")} busy={busy} />
            </div>
          )}
          {sel === "site_works" && (
            <div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Pit & Pipe Infrastructure Status">
                  <select data-testid="nbn-siteworks-status" className="bdx-input" disabled={readOnly} value={siteF.status} onChange={(e) => setSiteF({ status: e.target.value })}>
                    <option>Not Started</option><option>In Progress</option><option>Completed</option>
                  </select>
                </Field>
              </div>
              <div className="mt-4">
                <DocSlot projectId={project.id} linkedTo="nbn_design" category="NBN Design Drawing" label="Underground Design Drawing" docs={docs} onUploaded={refreshDocs} />
              </div>
              <SaveBar onSave={() => patch("site_works", siteF, "NBN pit & pipe infrastructure saved")} busy={busy} />
            </div>
          )}
          {sel === "completion" && (
            <div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Completion Status">
                  <select data-testid="nbn-completion-status" className="bdx-input" disabled={readOnly} value={compF.status} onChange={(e) => setCompF({ ...compF, status: e.target.value })}>
                    <option>Not Started</option><option>In Progress</option><option>Completed</option>
                  </select>
                </Field>
                <Field label="Completion Date"><input data-testid="nbn-completion-date" type="date" className="bdx-input" disabled={readOnly} value={compF.completion_date} onChange={(e) => setCompF({ ...compF, completion_date: e.target.value })} /></Field>
              </div>
              <div className="mt-4">
                <DocSlot projectId={project.id} linkedTo="nbn_completion" category="NBN Form 1 & Form 2 Completion" label="Form 1 & Form 2 Completion Document" docs={docs} onUploaded={refreshDocs} />
              </div>
              <SaveBar onSave={() => patch("completion", compF, "NBN completion saved")} busy={busy} />
            </div>
          )}
          {sel === "pipe_stringing" && (
            <div>
              <p className="text-sm text-gray-700 mb-3">Have infrastructure pipes been stringed?</p>
              <div className="flex gap-2">
                {["Pending", "Yes", "No"].map((opt) => (
                  <button key={opt} data-testid={`nbn-stringing-${opt.toLowerCase()}`} disabled={readOnly || busy}
                    onClick={() => patch("pipe_stringing", { value: opt }, `Pipe stringing set to ${opt}`)}
                    className={`bdx-btn ${nbn.pipe_stringing === opt
                      ? opt === "Yes" ? "bg-emerald-600 text-white border-emerald-600"
                        : opt === "No" ? "bg-red-600 text-white border-red-600"
                          : "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Drawer>
      )}

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
